import { useTranslation } from 'react-i18next';
import type { Id, Vehicle } from '@hero/schema';
import { useVehicleCreateManager, UseVehicleFormOptions } from '@hero/web-hooks';
import { ModalForm } from '../../../app/Modal';
import { VehicleForm } from '../Form/VehicleForm';

export interface VehicleEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExited?: () => void;
  onVehicleSaved?: (vehicle: Vehicle) => void;
  vehicleId?: Id;
  allowOutsideClickClose?: boolean;
  defaultValues?: UseVehicleFormOptions['defaultValues'];
}

export function VehicleEditModal({
  isOpen,
  onClose,
  onExited,
  onVehicleSaved,
  vehicleId,
  allowOutsideClickClose = true,
  defaultValues,
}: VehicleEditModalProps) {
  const { t } = useTranslation(['m-vehicle', 'c-main']);
  const { vehicleQuery, vehicleCreate, vehicleUpdate, formHook, reset, onSaveClick } =
    useVehicleCreateManager({
      onVehicleSaved,
      vehicleId,
      defaultValues,
    });

  const handleClose = () => {
    reset();
    onClose();
  };

  const isEdit = vehicleId != null;
  const isLoadingForm = isEdit && vehicleQuery.isLoading;
  const isLoading = vehicleCreate.isPending || vehicleUpdate.isPending;
  const isSuccess = vehicleCreate.isSuccess || vehicleUpdate.isSuccess;

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onExited={onExited}
      onSave={onSaveClick}
      title={isEdit ? t('modal.edit_vehicle_title') : t('modal.create_vehicle_title')}
      description={
        isEdit ? t('modal.edit_vehicle_description') : t('modal.create_vehicle_description')
      }
      isLoading={isLoading}
      isLoadingForm={isLoadingForm}
      isSuccess={isSuccess}
      allowOutsideClickClose={allowOutsideClickClose && !isLoading}
      hideFooter={false}
      size="lg"
      saveText={isEdit ? t('c-main:crud_buttons.update') : t('c-main:crud_buttons.create')}
      disableSave={!formHook.isFormValid}>
      <VehicleForm formHook={formHook} isLoading={isLoading} hideSubmitButton />
    </ModalForm>
  );
}
